import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase.ts';
import TopNavigation from '../components/Layout/TopNavigation';
import Dashboard from '../components/Layout/Dashboard';
import SyllableSelectionModal from '../components/Modals/SyllableSelectionModal';
import DifficultyModal from '../components/Modals/DifficultyModal';
import PinModal from '../components/Modals/PinModal';
import Profile from '../components/Profile/Profile';
import FloatingLautbilderButton from '../components/FloatingLautbilderButton';
import { isPhone } from '../utils/deviceDetection';

function DashboardPage({ setIsAuthenticated, setHasSubscription }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeGame, setActiveGame] = useState('home');
  const [showSyllableModal, setShowSyllableModal] = useState(false);
  const [selectedSyllables, setSelectedSyllables] = useState([]);
  const [showProfile, setShowProfile] = useState(false);
  const [showDifficultyModal, setShowDifficultyModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [difficulty, setDifficulty] = useState('medium');
  const [userData, setUserData] = useState(null);

  // TODO: Changes we need to tackle after merge conflict
  const [selectedGameInfo, setSelectedGameInfo] = useState({ id: null, name: '' });
 
  const fetchUserData = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
          // Set difficulty from user settings
          setDifficulty(data.difficulty || 'medium');
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    const view = searchParams.get('view');
    if (view && ['modules', 'materials', 'games', 'settings', 'profile', 'theory'].includes(view)) {
      if (view === 'profile') {
        setShowProfile(true);
        setActiveGame(null);
      } else {
        setActiveGame(view);
        setShowProfile(false);
      }
      // Clear the search params after setting the view
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const handleSyllableSelection = (syllables) => {
    setSelectedSyllables(syllables);
    setShowSyllableModal(false);
  };

  const handlePinSuccess = () => {
    setShowProfile(true);
    setActiveGame(null);
  };

  const handleGameSelect = (gameId) => {
    if (gameId === 'profile-edit') {
      // Redirect to settings page instead
      setActiveGame('settings');
      setShowProfile(false);
    } else if (gameId === 'home' || gameId === 'materials' || gameId === 'modules' || gameId === 'games' || gameId === 'settings' || gameId === 'subscription') {
      setActiveGame(gameId);
      setShowProfile(false);
      // Refresh user data when returning from settings
      if (activeGame === 'settings' && gameId !== 'settings') {
        fetchUserData();
      }
    } else if (gameId === 'memory' || gameId === 'story') {
      setActiveGame(gameId);
      setShowProfile(false);
      setDifficulty(null);
    } else {
      // For other games that need difficulty, use the saved difficulty
      setActiveGame(gameId);
      setShowProfile(false);
      setDifficulty(null);
    }
  };

  const handleGameSelectFromDashboard = (gameId, gameName) => {
    if (gameId === 'minimalpair') {
      setActiveGame(gameId);
      setShowProfile(false);
      setDifficulty(null);
    } else if (gameId === 'memory' && isPhone()) {
      // For MemoryGame on mobile, auto-select "Einfach" difficulty without showing modal
      setDifficulty('easy');
      setActiveGame(gameId);
      setShowProfile(false);
    } else {
      setSelectedGameInfo({ id: gameId, name: gameName });
      setShowDifficultyModal(true);
    }
  };

  const handleDifficultySelect = (selectedDifficulty) => {
    setDifficulty(selectedDifficulty);
    setActiveGame(selectedGameInfo.id);
    setShowProfile(false);
  };

  const getGameName = (gameId) => {
    const names = {
      memory: 'Zwillingspaare finden',
      story: 'Geschichte erzählen',
      wordmatch: 'Wörter verbinden'
    };
    return names[gameId] || '';
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setHasSubscription(false);
    navigate('/login');
  };

  const handleSettingsSaved = () => {
    // Refresh user data when settings are saved
    fetchUserData();
  };

  return (
    <div className="app-container">
      <TopNavigation activeGame={activeGame} />
      <main className={`main-content ${activeGame === 'wordmatch' ? 'wordmatch-game' : ''} ${activeGame === 'subscription' ? 'subscription-page' : ''}`}>
        {showProfile ? (
          <Profile />
        ) : (
          <Dashboard 
            activeGame={activeGame} 
            selectedSyllables={selectedSyllables}
            onGameSelect={handleGameSelectFromDashboard}
            difficulty={difficulty}
            setActiveGame={handleGameSelect}
            userData={userData}
            onSettingsSaved={handleSettingsSaved}
          />
        )}
      </main>
      
      <SyllableSelectionModal
        show={showSyllableModal}
        onHide={() => setShowSyllableModal(false)}
        onSelect={handleSyllableSelection}
      />

      <DifficultyModal
        show={showDifficultyModal}
        onHide={() => setShowDifficultyModal(false)}
        onSelect={handleDifficultySelect}
        gameName={selectedGameInfo.name}
      />

      <PinModal
        show={showPinModal}
        onHide={() => setShowPinModal(false)}
        onSuccess={handlePinSuccess}
      />

      <FloatingLautbilderButton />
    </div>
  );
}

export default DashboardPage;